import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: React.ReactNode;
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  show?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  children, 
  text, 
  position = 'right',
  show
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef<HTMLDivElement>(null);

  const updateTooltipPosition = () => {
    const shouldShow = show !== undefined ? show : isVisible;
    if (triggerRef.current && shouldShow) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 120; // Approximate tooltip width
      const tooltipHeight = 32; // Height for tooltip content + arrow
      
      let left = 0;
      let top = 0;
      
      switch (position) {
        case 'right':
          left = rect.right + 12;
          top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          break;
        case 'left':
          left = rect.left - tooltipWidth - 12;
          top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          break;
        case 'top':
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          top = rect.top - tooltipHeight - 12;
          break;
        case 'bottom':
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          top = rect.bottom + 12;
          break;
      }
      
      // Ensure tooltip doesn't go off-screen
      if (left < 10) left = 10;
      if (left + tooltipWidth > window.innerWidth - 10) left = window.innerWidth - tooltipWidth - 10;
      if (top < 10) top = 10;
      if (top + tooltipHeight > window.innerHeight - 10) top = window.innerHeight - tooltipHeight - 10;
      
      setTooltipStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 9999,
        pointerEvents: 'none'
      });
    }
  };

  useEffect(() => {
    const shouldShow = show !== undefined ? show : isVisible;
    if (shouldShow) {
      updateTooltipPosition();
      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isVisible, show, position]);

  // Arrow component based on position
  const getArrow = () => {
    const arrowClasses = "w-0 h-0 border-transparent";
    
    switch (position) {
      case 'top':
        return (
          <div 
            className={`${arrowClasses} border-l-4 border-r-4 border-t-4 border-t-gray-800`}
            style={{ marginTop: '-1px' }}
          />
        );
      case 'bottom':
        return (
          <div 
            className={`${arrowClasses} border-l-4 border-r-4 border-b-4 border-b-gray-800`}
            style={{ marginBottom: '-1px' }}
          />
        );
      case 'left':
        return (
          <div 
            className={`${arrowClasses} border-t-4 border-b-4 border-l-4 border-l-gray-800`}
            style={{ marginLeft: '-1px' }}
          />
        );
      case 'right':
        return (
          <div 
            className={`${arrowClasses} border-t-4 border-b-4 border-r-4 border-r-gray-800`}
            style={{ marginRight: '-1px' }}
          />
        );
      default:
        return null;
    }
  };

  const shouldShow = show !== undefined ? show : isVisible;
  const tooltipElement = shouldShow ? (
    <div 
      className="pointer-events-none absolute z-20 transition-opacity duration-150"
      style={tooltipStyle}
    >
      <div className={`flex items-center ${
        position === 'top' ? 'flex-col' : 
        position === 'bottom' ? 'flex-col-reverse' : 
        position === 'left' ? 'flex-row' : 
        'flex-row-reverse'
      }`}>
        <div className="rounded bg-gray-800 p-2 text-center text-caption text-white whitespace-nowrap">
          {text}
        </div>
        {getArrow()}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div 
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={show === undefined ? () => setIsVisible(true) : undefined}
        onMouseLeave={show === undefined ? () => setIsVisible(false) : undefined}
      >
        {children}
      </div>
      
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
};
